/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * MobileNavDrawer (#683, W2) — below {@link NAV_MOBILE_BREAKPOINT} the eight-tab
 * bar is too cramped, so navigation moves into a slide-in drawer showing the
 * full group tree at once (there is no room for a two-level top-bar + sidebar
 * model on a phone).
 *
 * Takes the same pre-filtered `NavTab[]` as the top bar, so a role sees exactly
 * the same surfaces on mobile as on desktop (#683 D3).
 */

import React from 'react';
import { Box, Button, Flex, Image, Text } from '@chakra-ui/react';
import { NavLink } from 'react-router-dom';
import { useGuardedLinkProps } from '../ui/UnsavedWorkGuard';
import type { NavItem, NavTab } from '../../utils/rbac';
import { NavIcon } from './nav-icons';

export interface MobileNavDrawerProps {
  open: boolean;
  tabs: NavTab[];
  onClose: () => void;
}

/**
 * **Still a `NavLink`, and no longer the browser's navigation** (`#1245`).
 *
 * It stays an anchor because that is what it is — middle-click, copy-link and open-in-new-tab all
 * keep working, and `useGuardedLinkProps` deliberately lets those gestures through untouched. What it
 * stops is the plain click, which it re-issues through the shell's one guard so a drawer link and a
 * top-bar tab cannot answer differently.
 */
const DrawerLink: React.FC<{ item: NavItem; onNavigate: () => void }> = ({ item, onNavigate }) => (
  <NavLink
    to={item.path}
    {...useGuardedLinkProps(item.path, onNavigate)}
    style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}
  >
    {({ isActive }) => (
      <Flex
        align="center"
        gap={3}
        px={4}
        py={2.5}
        fontSize="sm"
        color={isActive ? 'white' : 'rgba(255,255,255,0.8)'}
        fontWeight={isActive ? '600' : '400'}
        bg={isActive ? 'rgba(104,60,216,0.28)' : 'transparent'}
        borderLeftWidth="3px"
        borderLeftColor={isActive ? 'accent.solid' : 'transparent'}
      >
        <NavIcon name={item.icon} size={17} />
        <Text whiteSpace="nowrap">{item.label}</Text>
      </Flex>
    )}
  </NavLink>
);

export const MobileNavDrawer: React.FC<MobileNavDrawerProps> = ({ open, tabs, onClose }) => (
  <Box position="fixed" inset={0} zIndex={100} pointerEvents={open ? 'auto' : 'none'}>
    <Box
      position="absolute"
      inset={0}
      bg="rgba(15,21,32,0.5)"
      opacity={open ? 1 : 0}
      transition="opacity 0.2s"
      onClick={onClose}
    />
    <Box
      as="aside"
      aria-label="Navigation"
      position="absolute"
      top={0}
      left={0}
      bottom={0}
      w="284px"
      maxW="85vw"
      bg="bg.nav"
      color="white"
      display="flex"
      flexDirection="column"
      boxShadow="xl"
      transform={open ? 'translateX(0)' : 'translateX(-100%)'}
      transition="transform 0.25s"
    >
      <Flex align="center" px={4} py={4} gap={2} borderBottomWidth="1px" borderBottomColor="rgba(255,255,255,0.1)">
        <Image src="/assets/logo-mark-white.svg" alt="bidBrain" w="26px" h="26px" />
        <Text fontWeight="bold" fontSize="sm" flex={1}>
          bidBrain
        </Text>
        <Button
          aria-label="Close navigation menu"
          variant="ghost"
          size="sm"
          color="white"
          _hover={{ bg: 'rgba(255,255,255,0.08)' }}
          onClick={onClose}
        >
          <NavIcon name="x" size={20} />
        </Button>
      </Flex>

      <Box flex={1} overflowY="auto" pt={2} pb={4}>
        {tabs.map((tab) => {
          if (tab.kind === 'locked') {
            return (
              <Flex key={tab.key} align="center" gap={2} px={4} pt={3} pb={1} color="rgba(255,255,255,0.35)">
                <NavIcon name="lock" size={12} />
                <Text fontSize="10.5px" fontWeight="600" letterSpacing="0.08em" textTransform="uppercase">
                  {tab.label}
                </Text>
              </Flex>
            );
          }
          if (tab.kind === 'direct') {
            return (
              <Box key={tab.key} pt={1}>
                <DrawerLink item={tab.item} onNavigate={onClose} />
              </Box>
            );
          }
          return (
            <Box key={tab.key} pt={2.5}>
              <Text
                px={4}
                pt={1.5}
                pb={1}
                fontSize="10.5px"
                fontWeight="600"
                letterSpacing="0.08em"
                textTransform="uppercase"
                color="rgba(255,255,255,0.45)"
              >
                {tab.group.label}
              </Text>
              {tab.items.map((item) => (
                <DrawerLink key={item.path} item={item} onNavigate={onClose} />
              ))}
            </Box>
          );
        })}
      </Box>
    </Box>
  </Box>
);
